<template>
  <div class="text-center">
    <ModalDialog :visible="show && !submissionError"
      :can-close="!submissionInProgress"
      @close="$emit('close')"
    >
      <div v-show="submissionInProgress">
        <div v-show="!captchaToken">
          <h4>Please check the following box as a security measure:</h4>

          <!-- Since reCAPTCHA is rendered within an iframe, which we can't access programmatically
            due to same-origin policy, the only way to be able to tab to it reliably within the
            focus trap is to have tabbable elements before and after.
            See https://github.com/focus-trap/focus-trap#use-predictable-elements-for-the-first-and-last-tabbable-elements-in-your-trap -->
          <div tabindex="0"></div>

          <ReCaptcha
            :sitekey="config.captchaSiteKey"
            class="mt-4 d-flex justify-content-center"
            @verified="captchaToken = $event"
            @error="captchaToken = null"
            @expired="captchaToken = null"
          />

          <!-- See comment above. -->
          <div tabindex="0"></div>
        </div>
        <div v-show="!!captchaToken">
          <div class="d-flex flex-column align-items-center gap-3">
            <LoadingSpinner />
            <h4><slot name="submitting-status">Working on it...</slot></h4>
          </div>
        </div>
      </div>

      <template v-if="!submissionInProgress && !submissionError">
        <slot v-bind="successPayload"></slot>
      </template>
    </ModalDialog>

    <ErrorModal :visible="show && !!submissionError"
      :can-close="true"
      @close="$emit('close')"
    >
      <h4 class="text-danger">
        <slot name="error-title" :error="submissionError">{{ submissionError }}</slot>
      </h4>

      <template #error-body>
        <slot name="error-body" :error="submissionError"></slot>

        <button v-if="canRetryFailure"
          type="button"
          class="btn btn-outline-primary mt-4"
          @click="submit"
        >Click here to try again</button>
      </template>
    </ErrorModal>
  </div>
</template>

<script>
import ModalDialog from './ModalDialog';
import ErrorModal from './ErrorModal';
import LoadingSpinner from '../LoadingSpinner';
import ReCaptcha from '../ReCaptcha';

export default {
  inject: ['config'],

  props: {
    canRetryFailure: {
      type: Boolean,
      default: () => true
    },

    show: {
      type: Boolean,
      default: () => true
    },

    /**
     * An async function that submits form data. Will be called with the CAPTCHA token
     * once the user completes the CAPTCHA, and again if the user retries upon failure.
     */
    submitFn: {
      type: Function,
      required: true
    }
  },

  components: {
    ErrorModal, LoadingSpinner, ModalDialog, ReCaptcha
  },

  data: () => ({
    captchaToken: null,
    submissionError: null,
    // The user has attempted to submit the form and is either verifying the CAPTCHA
    // or awaiting a server response.
    submissionInProgress: false,
    submissionSucceeded: false,
    successPayload: {}
  }),

  methods: {
    /**
     * Resolves with the CAPTCHA token when the user has verified the CAPTCHA and it
     * has not expired.
     * @returns {Promise} A Promise containing the current CAPTCHA token.
     */
    async checkCaptcha() {
      return new Promise((resolve) => {
        if (this.captchaToken) resolve(this.captchaToken);
        else {
          // The verification expires after a short timeout after which the token will
          // be cleared, so if there is no token wait for the user to re-verify.
          const removeWatch = this.$watch('captchaToken', (token) => {
            if (token) {
              resolve(token);
              removeWatch();
            }
          });
        }
      });
    },

    async submit() {
      this.submissionError = null;
      this.submissionInProgress = true;

      await this.checkCaptcha();

      try {
        const result = await this.submitFn(this.captchaToken);
        this.successPayload = result;
        this.$emit('success');
      } catch (e) {
        this.submissionError = e.message;
      } finally {
        this.submissionInProgress = false;
      }
    }
  },

  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) this.submit();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables";
@import "../../styles/animations";
</style>
